import { type ComponentPropsWithoutRef } from 'react';

import config from '@/config';
import { gql } from '@/generated';
import { type CustomQuestion_EditFragmentFragment } from '@/generated/graphql';

import FormFieldGroup from '../FormFieldGroup';
import AddressQuestion from './_components/AddressQuestion';
import ChoiceQuestion from './_components/ChoiceQuestion';
import CurrencyQuestion from './_components/CurrencyQuestion';
import DateQuestion from './_components/DateQuestion';
import NumberQuestion from './_components/NumberQueastion';
import PhoneQuestion from './_components/PhoneQuestion';
import ShortTextQuestion from './_components/ShortTextQuestion';
import LongTextQuestion from './_components/LongTextQuestion';
import WaiverQuestion from './_components/WaiverQuestion';
import YesNoQuestion from './_components/YesNoQuestion';

export const CustomQuestion_EditFragment = gql(/* GraphQL */ `
  fragment CustomQuestion_EditFragment on Question {
    id
    config
    helpText
    isRequired
    label
    options(where: { isEnabled: true }, order: "sort") {
      id
      label
    }
    type
  }
`);

interface FormCustomQuestionsProps extends ComponentPropsWithoutRef<typeof FormFieldGroup> {
  fieldPrefix?: string;
  questions?: CustomQuestion_EditFragmentFragment[];
}

const FormCustomQuestions = ({
  questions = [],
  fieldPrefix = '',
  ...props
}: FormCustomQuestionsProps) => {
  if (!questions || questions.length === 0) return null;
  return (
    <FormFieldGroup {...props}>
      {questions.map((question) => {
        const name = fieldPrefix + question.id;

        switch (question.type) {
          case 'address': {
            return <AddressQuestion key={name} name={name} {...question} />;
          }

          case 'choice': {
            return <ChoiceQuestion key={name} name={name} {...question} />;
          }

          case 'currency': {
            return <CurrencyQuestion key={name} name={name} {...question} />;
          }

          case 'date': {
            return <DateQuestion key={name} name={name} {...question} />;
          }

          case 'number': {
            return <NumberQuestion key={name} name={name} {...question} />;
          }

          case 'phone': {
            return <PhoneQuestion key={name} name={name} {...question} />;
          }

          case 'text_single_line': {
            return <ShortTextQuestion key={name} name={name} {...question} />;
          }

          case 'text_multiple_line': {
            return <LongTextQuestion key={name} name={name} {...question} />;
          }

          case 'waiver_simple': {
            return <WaiverQuestion key={question.id} name={name} {...question} />;
          }

          case 'yes_no': {
            return <YesNoQuestion key={name} name={name} {...question} />;
          }

          default: {
            if (config('/debug')) console.error('Unknown question type', question.type);
            return null;
          }
        }
      })}
    </FormFieldGroup>
  );
};

export default FormCustomQuestions;
